import axiosInstance from '../api/axiosInstance';
import {
    CreatePropertyDto,
    PropertyListResponseDto,
    PropertySingleResponseDto,
    PropertyFilterDto,
    MediaResponseDto
} from '../dtos/PropertyDto';
import { GenericResponseDto } from '../dtos/CategoryDto';

export class PropertyRepository {
    static async getAll(filters?: PropertyFilterDto): Promise<PropertyListResponseDto> {
        const response = await axiosInstance.get<PropertyListResponseDto>('/admin/properties', { params: filters });
        return response.data;
    }
    static async getById(id: number): Promise<PropertySingleResponseDto> {
        const response = await axiosInstance.get<PropertySingleResponseDto>(`/admin/properties/${id}`);
        return response.data;
    }

    static async getPropertyImages(id: number): Promise<any> {
        const response = await axiosInstance.get(`/properties/getimgas/${id}`);
        return response.data;
    }

    static preparePayload(data: CreatePropertyDto) {
        const payload: any = {
            category_id: data.category_id,
            user_id: data.user_id,
            contact_info: data.contact_info,
            is_active: data.is_active ? 1 : 0,

            // Pricing
            price_daily: Number(data.price_daily) || 0,
            price_monthly: Number(data.price_monthly) || 0,
            price_yearly: Number(data.price_yearly) || 0,

            // Location
            lat: data.lat,
            lng: data.lng,
            address_ar: data.ar.address || '',
            address_en: data.en.address || '',
            address_tr: data.tr.address || '',

            // Multi-language nested objects
            ar: {
                title: data.ar.title,
                description: data.ar.description
            },
            en: {
                title: data.en.title || data.ar.title,
                description: data.en.description || data.ar.description
            },
            tr: {
                title: data.tr.title || data.ar.title,
                description: data.tr.description || data.ar.description
            },

            // Unified Features List (Core + Additional)
            additional_features: [
                { label: 'rooms', value: Number(data.rooms) || 0 },
                { label: 'bathrooms', value: Number(data.bathrooms) || 0 },
                { label: 'area', value: Number(data.area) || 0 },
                ...(data.additional_features || []).map(f => {
                    if (typeof f === 'string') return { label: f, value: true };
                    return f; // Already an object
                })
            ],

            // Keep top-level for backward compatibility/filtering if needed
            rooms: Number(data.rooms) || 0,
            bathrooms: Number(data.bathrooms) || 0,
            area: Number(data.area) || 0,
        };
        return payload;
    }

    static async create(data: CreatePropertyDto): Promise<PropertySingleResponseDto> {
        const payload = this.preparePayload(data);
        console.log('🚀 Property Submission Payload (POST):', JSON.stringify(payload, null, 2));
        const response = await axiosInstance.post<PropertySingleResponseDto>('/admin/properties', payload);
        return response.data;
    }

    static async update(id: number, data: CreatePropertyDto): Promise<PropertySingleResponseDto> {
        const payload = this.preparePayload(data);
        console.log('🚀 Property Submission Payload (PUT):', JSON.stringify(payload, null, 2));
        const response = await axiosInstance.put<PropertySingleResponseDto>(`/admin/properties/${id}`, payload);
        return response.data;
    }

    static async delete(id: number): Promise<GenericResponseDto> {
        const response = await axiosInstance.delete<GenericResponseDto>(`/admin/properties/${id}`);
        return response.data;
    }

    static async uploadMediaAsync(propertyId: number, formData: FormData, onProgress?: (percent: number) => void): Promise<MediaResponseDto> {
        const response = await axiosInstance.post<MediaResponseDto>(
            `/admin/media/manageMedia/${propertyId}`,
            formData,
            {
                headers: {
                    'Accept': 'application/json',
                },
                onUploadProgress: (progressEvent) => {
                    if (onProgress && progressEvent.total) {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        onProgress(percentCompleted);
                    }
                }
            }
        );
        return response.data;
    }

    static async deleteMedia(mediaId: number): Promise<GenericResponseDto> {
        const response = await axiosInstance.delete<GenericResponseDto>(`/admin/media/${mediaId}`);
        return response.data;
    }
}
